import React, { useState, useEffect, useRef } from 'react';
import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch,
  Configure,
  connectHits,
  connectStats,
} from 'react-instantsearch-dom';
import $ from 'jquery';

// Components
import FeaturedList from './FeaturedList';
import UpcomingList from './UpcomingList';
import RecipeCards from './RecipeCards';
import ListPager from './ListPager';
import Loader from './Loader';

// Config
import {
  AJAX_URL,
  TAXONOMY,
  TEMPLATE,
  COOP_ID,
  COOP_NAME,
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_KEY,
  ALGOLIA_INDEX_PREFIX,
} from '../../config/index.js';

const el = document.getElementById('taxonomy-list-app');
const mainArticles = el ? JSON.parse(el.dataset.articleIds || '[]') : [];
const pinnedData = el ? JSON.parse(el.dataset.pinnedData || '[]') : [];
const pinnedIds = pinnedData.map((p) => p.id);
const isRecipesPage = window.hasOwnProperty('taxConfig');
const isSubTemplate = TEMPLATE === 'templates/page-category-sub.php';

// ─── Algolia setup ────────────────────────────────────────────────────────────

const searchClient =
  ALGOLIA_APP_ID && ALGOLIA_SEARCH_KEY
    ? algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
    : null;

const buildFilters = () => {
  const parts = [];
  if (TAXONOMY && TAXONOMY.name) {
    parts.push(`taxonomies.category:"${TAXONOMY.name}"`);
  }
  if (mainArticles.length > 0) {
    parts.push(mainArticles.map((id) => `NOT objectID:${id}`).join(' AND '));
  }
  if (pinnedIds.length > 0) {
    parts.push(pinnedIds.map((id) => `NOT objectID:${id}`).join(' AND '));
  }
  // Filter by visibility. Public articles have visible_to:"all"; coop-only articles
  // have visible_to set to their coop name(s). This avoids NOT-inside-OR syntax
  // which Algolia doesn't support.
  if (COOP_NAME) {
    parts.push(`(visible_to:"all" OR visible_to:"${COOP_NAME}")`);
  } else {
    parts.push('visible_to:"all"');
  }
  return parts.join(' AND ');
};

const algoliaFilters = buildFilters();

const mapHit = (hit) => {
  let kicker = hit.kicker;
  if (hit.kicker_field === '' && COOP_NAME) {
    kicker = `${COOP_NAME} News`;
  }
  return {
    id: hit.objectID,
    url: hit.permalink,
    image: hit.post_image,
    title: hit.post_title,
    kicker,
    subheader: hit.post_excerpt,
    sponsored: hit.sponsored || false,
    byline: hit.by_line || '',
    byline_credit: hit.by_line_credit || 'By',
  };
};

// ─── Connected components ─────────────────────────────────────────────────────

const ArticleHits = connectHits(({ hits, onFirstLoad, contests, coops, pinnedArticles, currentPage, limit = 12 }) => {
  const loaded = useRef(false);

  useEffect(() => {
    if (!loaded.current) {
      loaded.current = true;
      onFirstLoad();
    }
  }, [hits]);

  // Prepend pinned articles only on the first page, capped to limit
  const pagedArticles = [
    ...(currentPage === 0 ? pinnedArticles : []),
    ...hits.map(mapHit),
  ].slice(0, limit);

  if (isSubTemplate) {
    return (
      <>
        <FeaturedList taxonomyArticle={pagedArticles.slice(0, 2)} contests={contests} coops={coops} />
        <div className="container container--xl">
          <UpcomingList taxonomyArticles={pagedArticles.slice(2)} ads={[]} />
        </div>
      </>
    );
  }

  return (
    <div className="container container--xl">
      <UpcomingList taxonomyArticles={pagedArticles} ads={[]} />
    </div>
  );
});

const StatsWrapper = connectStats(({ nbHits, children }) => children({ nbHits }));

// ─── Main component ───────────────────────────────────────────────────────────

export default function TaxonomyList() {
  const [isLoading, setLoading] = useState(true);
  const [limit, setLimit] = useState(12);
  const [page, setPage] = useState(0); // 0-indexed, matches Algolia's page parameter
  const pageInteracted = useRef(false);
  const [contests, setContests] = useState([]);
  const [coops, setCoops] = useState([]);
  const [pinnedArticles, setPinnedArticles] = useState([]);

  useEffect(() => {
    if (!pinnedIds.length || !searchClient) return;
    searchClient
      .search([{
        indexName: `${ALGOLIA_INDEX_PREFIX}articles`,
        query: '',
        params: {
          filters: pinnedIds.map((id) => `objectID:${id}`).join(' OR '),
          hitsPerPage: pinnedIds.length,
        },
      }])
      .then(({ results }) => {
        const hits = results[0].hits;
        const hitMap = Object.fromEntries(hits.map((h) => [h.objectID, h]));

        // Replicate wl_format_articles_by_lock_status:
        // 1. Separate locked vs non-locked
        const lockedItems = [];
        const shuffleItems = [];
        pinnedData.forEach(({ id, lock, index }) => {
          const hit = hitMap[String(id)];
          if (!hit) return;
          if (lock) {
            lockedItems.push({ hit, index });
          } else {
            shuffleItems.push(hit);
          }
        });

        // 2. Shuffle non-locked articles
        for (let i = shuffleItems.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffleItems[i], shuffleItems[j]] = [shuffleItems[j], shuffleItems[i]];
        }

        // 3. Splice locked articles back into their original index positions
        lockedItems.forEach(({ hit, index }) => {
          shuffleItems.splice(index, 0, hit);
        });

        setPinnedArticles(shuffleItems.map(mapHit));
      })
      .catch((err) => console.warn('[TaxonomyList] Failed to fetch pinned articles:', err));
  }, []);

  useEffect(() => {
    document.addEventListener(
      'click',
      () => {
        pageInteracted.current = true;
      },
      { once: true }
    );
  }, []);

  useEffect(() => {
    if (!isSubTemplate) return;
    $.ajax({
      type: 'POST',
      url: `${AJAX_URL}post_wlQueryTaxonomy`,
      dataType: 'json',
      encode: true,
      data: {
        terms: TAXONOMY ? TAXONOMY.slug : '',
        coop_id: COOP_ID,
        post_type: 'post',
        exclude_posts: [],
        offset: 0,
        queryAmount: 0,
      },
      contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
    })
      .then(({ data }) => {
        if (data.contests) setContests(data.contests);
        if (data.coop) setCoops(data.coop);
      })
      .catch((err) => console.warn('[TaxonomyList] Failed to fetch contests/coop:', err));
  }, []);

  if (isRecipesPage) {
    return <RecipesTaxonomyList />;
  }

  if (!searchClient) return null;

  const scrollToTop = () => {
    if (!pageInteracted.current) return;
    const scrollOffset = document.body.classList.contains('admin-bar') ? 105 : 70;
    window.scrollTo({
      top:
        window.pageYOffset +
        parseInt(el.getBoundingClientRect().top) -
        scrollOffset,
      behavior: 'smooth',
    });
  };

  const handleSetLimit = (newLimit) => {
    setLimit(newLimit);
    setPage(0);
  };

  const handleSetPage = (idx) => {
    setPage(idx);
    scrollToTop();
  };

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={`${ALGOLIA_INDEX_PREFIX}articles`}
    >
      <Configure hitsPerPage={limit} filters={algoliaFilters} page={page} />
      {isLoading && <Loader />}
      <div
        style={
          isLoading ? { visibility: 'hidden', height: 0, overflow: 'hidden' } : {}
        }
      >
        <ArticleHits
          onFirstLoad={() => {
            setLoading(false);
            setTimeout(() => window.initializeAds && window.initializeAds(), 3000);
          }}
          contests={contests}
          coops={coops}
          pinnedArticles={pinnedArticles}
          currentPage={page}
          limit={limit}
        />
        <StatsWrapper>
          {({ nbHits }) => {
            const totalPages = Math.ceil(nbHits / limit);
            return (
              <div className="container container--xl">
                <ListPager
                  changeLimit={handleSetLimit}
                  limit={limit}
                  setPagerIndex={handleSetPage}
                  nextPage={() => handleSetPage(Math.min(page + 1, totalPages - 1))}
                  prevPage={() => handleSetPage(Math.max(page - 1, 0))}
                  pagerIndex={page}
                  totalEvents={nbHits}
                  totalPages={totalPages}
                  paginatedItemCount={Math.min((page + 1) * limit, nbHits)}
                />
              </div>
            );
          }}
        </StatsWrapper>
      </div>
    </InstantSearch>
  );
}

// ─── Recipes fallback (AJAX) ──────────────────────────────────────────────────

let pageWasInteractedWith = false;

function RecipesTaxonomyList() {
  const [isLoading, setLoading] = useState(true);
  const [eventList, setEventList] = useState([]);
  const [ads, setAds] = useState([]);
  const [limit, setLimit] = useState(12);
  const [pagerIndex, setPagerIndex] = useState(0);
  const [paginatedEvents, setPaginatedEvents] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingFinished, setLoadingFinished] = useState(false);
  const remainingPosts = useRef([]);
  const queryAmount = 400;

  const query = {
    terms: TAXONOMY ? TAXONOMY.slug : '',
    coop_id: COOP_ID,
    post_type: window.taxConfig.post_type,
    exclude_posts: mainArticles,
  };

  $(document).one('click', () => {
    pageWasInteractedWith = true;
  });

  const handlePagination = (list, currentIndex) => {
    const pages = Math.ceil(list.length / limit);
    setTotalPages(pages);

    if (currentIndex >= pages) {
      setPagerIndex(0);
      return;
    }
    if (currentIndex < 0) {
      setPagerIndex(pages - 1);
      return;
    }

    const start = limit * currentIndex;
    setPaginatedEvents(list.slice(start, start + limit));

    if (pagerIndex || (!pagerIndex && pageWasInteractedWith)) {
      const scrollOffset = document.body.classList.contains('admin-bar') ? 105 : 70;
      window.scrollTo({
        top:
          window.pageYOffset +
          parseInt(el.getBoundingClientRect().top) -
          scrollOffset,
        behavior: 'smooth',
      });
    }
  };

  const pullRemainingPosts = (offset) => {
    $.ajax({
      type: 'POST',
      url: `${AJAX_URL}post_wlQueryTaxonomy`,
      dataType: 'json',
      encode: true,
      data: { ...query, offset, queryAmount },
      contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
    })
      .then(({ success, data: { taxonomy_posts } }) => {
        if (success) {
          remainingPosts.current.push(...taxonomy_posts);
          pullRemainingPosts(offset + queryAmount);
        } else {
          setEventList([...remainingPosts.current]);
          setLoadingFinished(true);
        }
      })
      .catch((err) => console.log(err.message));
  };

  useEffect(() => {
    setLoading(true);
    $.ajax({
      type: 'POST',
      url: `${AJAX_URL}post_wlQueryTaxonomy`,
      dataType: 'json',
      encode: true,
      data: { ...query, offset: 0 },
      contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
    })
      .then(({ data: { taxonomy_posts, ads: adsData } }) => {
        remainingPosts.current.push(...taxonomy_posts);
        setEventList([...taxonomy_posts]);
        setAds(adsData);
        pullRemainingPosts(50);
      })
      .catch((err) => console.log(err.message));
  }, []);

  useEffect(() => {
    if (eventList.length > 0) setLoading(false);
    handlePagination(eventList, pagerIndex);
  }, [eventList]);

  useEffect(() => {
    handlePagination(eventList, pagerIndex);
  }, [pagerIndex, limit]);

  return isLoading ? (
    <Loader />
  ) : (
    <div className="container container--xl">
      <RecipeCards recipes={paginatedEvents} ads={ads} />
      <ListPager
        changeLimit={setLimit}
        limit={limit}
        setPagerIndex={setPagerIndex}
        nextPage={() => setPagerIndex(pagerIndex + 1)}
        prevPage={() => setPagerIndex(pagerIndex - 1)}
        pagerIndex={pagerIndex}
        totalEvents={loadingFinished ? eventList.length : 'loading'}
        totalPages={totalPages}
        paginatedItemCount={paginatedEvents.length * (pagerIndex + 1)}
      />
    </div>
  );
}
